import type { SelfView } from "@bezbolisno/contracts/telegram";
import { t } from "@bezbolisno/i18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppointmentList } from "./components/AppointmentList";
import { InvoiceList } from "./components/InvoiceList";
import { NextAppointment } from "./components/NextAppointment";
import { PackageList } from "./components/PackageList";
import { reportError } from "./instrument";
import { createPatientApi, NoInitDataError, NotLinkedError } from "./lib/api";
import { readRawInitData } from "./lib/telegram";

/**
 * Loading → (not linked | failed | home). "Not linked" covers both answers that mean the same
 * thing to the patient: the API does not know this Telegram account, or there is no Telegram
 * account at all (the page was opened in a plain browser).
 */
type State =
  | { kind: "loading" }
  | { kind: "not-linked" }
  | { kind: "failed" }
  | { kind: "ready"; view: SelfView };

export function App() {
  const api = useMemo(() => createPatientApi({ readInitData: readRawInitData }), []);
  const [state, setState] = useState<State>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      setState({ kind: "ready", view: await api.me() });
    } catch (error) {
      const unlinked = error instanceof NotLinkedError || error instanceof NoInitDataError;
      if (!unlinked) reportError(error, { stage: "load" });
      setState({ kind: unlinked ? "not-linked" : "failed" });
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") {
    return (
      <main className="tg-centered tg-hint" aria-busy="true">
        {t.telegram.loading}
      </main>
    );
  }

  if (state.kind === "not-linked") {
    return (
      <main className="tg-centered">
        <div>
          <h1 style={{ fontSize: "1.125rem", margin: "0 0 0.5rem" }}>
            {t.telegram.notLinkedTitle}
          </h1>
          <p className="tg-hint" style={{ margin: 0 }}>
            {t.telegram.notLinkedBody}
          </p>
        </div>
      </main>
    );
  }

  if (state.kind === "failed") {
    return (
      <main className="tg-centered">
        <div>
          <h1 style={{ fontSize: "1.125rem", margin: "0 0 0.5rem" }}>{t.telegram.failedTitle}</h1>
          <p className="tg-hint" style={{ margin: "0 0 1rem" }}>
            {t.telegram.failedBody}
          </p>
          <button type="button" className="tg-btn" onClick={() => void load()}>
            {t.errors.retry}
          </button>
        </div>
      </main>
    );
  }

  const { view } = state;
  return (
    <main className="tg-app">
      <header>
        <div className="tg-hint" style={{ fontSize: "0.8125rem" }}>
          {t.telegram.appTitle}
        </div>
        <h1 style={{ fontSize: "1.375rem", margin: 0 }}>{view.patient.name}</h1>
      </header>
      <NextAppointment appointment={view.nextAppointment} />
      <AppointmentList appointments={view.appointments} />
      <PackageList packages={view.packages} />
      <InvoiceList invoices={view.invoices} />
    </main>
  );
}
